import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Chatbot from "./Chatbot";
import API_BASE_URL from "./config";
import "./ChatWidget.css";

function normalizeWindowSize(raw) {
  if (raw == null || raw === "") return "medium";
  const v = String(raw).toLowerCase().replace(/[\s_-]/g, "");
  if (v === "small") return "small";
  if (v === "fullscreen" || v === "full") return "fullscreen";
  return "medium";
}

function getSettingsBaseUrls() {
  const bases = new Set();
  if (API_BASE_URL) bases.add(String(API_BASE_URL).replace(/\/$/, ""));
  if (typeof window !== "undefined") {
    const pub = process.env.PUBLIC_URL || "/chatbot";
    bases.add(`${window.location.origin}${pub}`.replace(/\/$/, ""));
    bases.add(window.location.origin);
  }
  return [...bases];
}

function getWidgetPathCandidates(base, pathWithQuery) {
  const cleanedBase = String(base || "").replace(/\/$/, "");
  const cleanedPath = String(pathWithQuery || "").replace(/^\//, "");
  const primary = `${cleanedBase}/api/widget/${cleanedPath}`;
  // Only add /chatbot/api/... when base is the origin (e.g. http://localhost:8000), not when base
  // already ends with /chatbot — otherwise we hit /chatbot/chatbot/... (wrong path + CORS noise).
  const candidates = [primary];
  if (!/\/chatbot$/i.test(cleanedBase)) {
    candidates.push(`${cleanedBase}/chatbot/api/widget/${cleanedPath}`);
  }
  return candidates;
}

async function fetchWidgetJsonWithFallback(base, pathWithQuery) {
  const candidates = getWidgetPathCandidates(base, pathWithQuery);
  let lastResponse = null;
  for (const url of candidates) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        return { ok: true, data };
      }
      lastResponse = res;
    } catch (_) {
      // try next candidate
    }
  }
  return { ok: false, status: lastResponse ? lastResponse.status : 0, data: null };
}

export default function ChatWidget() {
  const embedParam =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1";

  const isEmbedded =
    typeof window !== "undefined" && (embedParam || window.self !== window.top);

  /** In integration iframe, host already opened the panel — open chat on first paint (avoids empty white iframe). */
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return embedParam || window.self !== window.top;
  });
  /** Keep Chatbot mounted after first open so close/reopen preserves the in-memory session. */
  const [chatPanelMounted, setChatPanelMounted] = useState(() => {
    if (typeof window === "undefined") return false;
    return embedParam || window.self !== window.top;
  });
  const [windowSize, setWindowSize] = useState("medium");
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const postedSizeRef = useRef(null);

  const launcherOwlUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    return `${pub}/assets/Hailuo-Video-Just-make-it-as-a-unscreen.gif`;
  }, []);

  const postToParent = useCallback((payload) => {
    if (!isEmbedded || !window.parent) return;
    try {
      window.parent.postMessage(payload, "*");
    } catch (_) {
      /* ignore */
    }
  }, [isEmbedded]);

  // Stable height chain inside iframe; always clear when not embedded (fixes Test Widget after embed preview).
  useEffect(() => {
    if (isEmbedded) {
      document.documentElement.classList.add("chat-widget-embed");
      document.body.classList.add("chat-widget-embed");
    } else {
      document.documentElement.classList.remove("chat-widget-embed");
      document.body.classList.remove("chat-widget-embed");
    }
    return () => {
      document.documentElement.classList.remove("chat-widget-embed");
      document.body.classList.remove("chat-widget-embed");
    };
  }, [isEmbedded]);

  // Mobile: match embed sheet (max-width 768) — laptop/desktop unchanged above 768
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobileViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isEmbedded || !isMobileViewport) return;
    document.documentElement.classList.add("chat-widget-iframe-mobile");
    document.body.classList.add("chat-widget-iframe-mobile");
    return () => {
      document.documentElement.classList.remove("chat-widget-iframe-mobile");
      document.body.classList.remove("chat-widget-iframe-mobile");
    };
  }, [isEmbedded, isMobileViewport]);

  useEffect(() => {
    const size = normalizeWindowSize(windowSize);
    if (postedSizeRef.current === size) return;
    postedSizeRef.current = size;
    postToParent({ type: "CHATBOT_WIDGET_SIZE", size });
  }, [windowSize, postToParent]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        let botId = urlParams.get("bot_id");

        if (!botId) {
          try {
            for (const base of getSettingsBaseUrls()) {
              const botsRes = await fetchWidgetJsonWithFallback(base, "default-bot");
              if (botsRes.ok) {
                const botsData = botsRes.data;
                if (botsData?.success && botsData?.bot_id) {
                  botId = botsData.bot_id;
                  const newUrl = new URL(window.location.href);
                  newUrl.searchParams.set("bot_id", botId);
                  window.history.replaceState({}, "", newUrl);
                  break;
                }
              }
            }
          } catch (e) {
            console.warn("[ChatWidget] Could not fetch default bot:", e);
          }
        }

        botId = botId || "MAIN";

        let settings = null;
        for (const base of getSettingsBaseUrls()) {
          try {
            const result = await fetchWidgetJsonWithFallback(
              base,
              `settings?bot_id=${encodeURIComponent(botId)}`
            );
            if (result.ok) {
              const data = result.data;
              if (data?.success && data?.settings) {
                settings = data.settings;
                break;
              }
            }
          } catch (_) {
            /* try next base */
          }
        }

        if (settings && settings.window_size != null) {
          setWindowSize(normalizeWindowSize(settings.window_size));
        }
      } catch (e) {
        console.error("[ChatWidget] Error loading settings:", e);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (isOpen) setChatPanelMounted(true);
  }, [isOpen]);

  const showChatPanel = isEmbedded || isOpen;
  const chatPanelHidden = chatPanelMounted && !isEmbedded && !isOpen;

  const rootClass = [
    "chat-widget-root",
    isEmbedded ? "chat-widget-embed" : "",
    isEmbedded && isMobileViewport ? "chat-widget-embed-mobile" : "",
    showChatPanel ? "chat-widget-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleCloseChat = () => {
    if (isEmbedded) {
      // Host widget.js hides the iframe — keep chat mounted for instant reopen
      postToParent({ type: "CHATBOT_CLOSE" });
      return;
    }
    setIsOpen(false);
  };

  // Host owl click re-shows iframe — ensure chat is open inside embed
  useEffect(() => {
    if (!isEmbedded) return;
    const onHostMessage = (event) => {
      const t = event.data?.type;
      if (t === "CHATBOT_OPEN" || t === "WETANO_CHAT_OPEN") {
        setIsOpen(true);
      }
    };
    window.addEventListener("message", onHostMessage);
    return () => window.removeEventListener("message", onHostMessage);
  }, [isEmbedded]);

  // Safety: keep standalone/Test Widget pinned bottom-right even if global CSS overrides leak in.
  const standaloneSizeStyle =
    !isEmbedded
      ? (isMobileViewport
          ? (windowSize === "small"
              ? { width: "90vw", height: "55vh", maxWidth: "400px", maxHeight: "calc(100vh - 120px)" }
              : windowSize === "fullscreen"
                ? { width: "100vw", height: "100dvh", maxHeight: "100dvh" }
                : { width: "90vw", height: "60vh", maxWidth: "400px", maxHeight: "calc(100vh - 120px)" })
          : (windowSize === "small"
              ? { width: "min(320px, calc(100vw - 40px))", height: "min(420px, calc(100vh - 150px))" }
              : windowSize === "fullscreen"
                ? { width: "100vw", height: "100dvh", maxHeight: "100dvh" }
                : { width: "min(350px, calc(100vw - 40px))", height: "min(500px, calc(100vh - 150px))" }))
      : undefined;

  const standalonePopupStyle =
    !isEmbedded && windowSize !== "fullscreen"
      ? (isMobileViewport
          ? { top: "auto", left: "auto", right: "5vw", bottom: "110px" }
          : { top: "auto", left: "auto", right: "20px", bottom: "130px" })
      : undefined;

  const resolvedStandalonePopupStyle =
    !isEmbedded
      ? {
          position: "fixed",
          zIndex: 9999,
          ...(windowSize === "fullscreen"
            ? { inset: 0, right: 0, bottom: 0, top: 0, left: 0, borderRadius: 0 }
            : standalonePopupStyle || {}),
          ...(standaloneSizeStyle || {}),
        }
      : undefined;

  return (
    <div className={rootClass}>
      {chatPanelMounted && (
        <div
          className={`chatbot-popup ${windowSize}${chatPanelHidden ? " chatbot-popup--closed" : ""}`}
          style={chatPanelHidden ? { display: "none" } : resolvedStandalonePopupStyle}
          aria-hidden={chatPanelHidden}
        >
          <Chatbot onClose={handleCloseChat} embedded={isEmbedded} />
        </div>
      )}

      {!isEmbedded && (
      <button
        type="button"
        className="chatbot-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        <img
          src={launcherOwlUrl}
          alt=""
          className="chatbot-logo"
          loading="eager"
          decoding="async"
        />
      </button>
      )}
    </div>
  );
}
